import { Component, ElementRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription, Unsubscribable } from 'rxjs';
import { DateTimeRange } from '@nitro/reactor/datetime/types';
import { TimeRange } from '../../models/time-range';
import { TimePickerNames, TimePickerOption, TimeRangeGranularity, TimeRangeKey } from './types';
import { DateFormatterService } from '../../providers/date.formatter/date.formatter.service';
import { TimePickerService } from '../../providers/time-picker/time-picker.service';
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { Moment } from 'moment';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  TIMEPICKER_FUSION_OPTIONS,
  TIMEPICKER_OPTIONS
} from '../../providers/time-picker/timePickerOptions';
import { ValuesExchangeProvider } from '../../providers/values.exchange/values.exchange.provider';
import { NotificationService } from '../notifications/notifications.service';
import { AutoRefreshService } from '../../services/auto-refresh/auto-refresh.service';
import { UserService } from '../../providers/user/user.service';

const DEFAULT_GRANULARITY: TimeRangeGranularity[] = [
  {
    minDuration: 0,
    maxDuration: Number.POSITIVE_INFINITY,
    truncateTime: 5 * 60 * 1000
  }
];

@Component({
  selector: 'time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss']
})
export class TimePickerComponent implements OnInit, OnDestroy {
  @Input() minDate?: Moment;
  @Input() timePickerOptions: TimePickerOption[];
  @Input() granularity: TimeRangeGranularity[];
  @Input() utc: boolean;
  timePickerSelection: TimePickerOption;
  timePickerSubscription: Unsubscribable;
  timePickerEventSubscription: Unsubscribable;
  showTimeRangeSelector = false;
  maxDate = moment();
  selectedListItemSubscription: Subscription;
  selectedTimeItemSubscription: Unsubscribable;
  autoRefreshSubscription: Unsubscribable;
  pageTriagedSubscription: Unsubscribable;
  showCalendar = false;
  calendarModel: DateTimeRange<Moment>;
  showTimeRangeError: boolean;
  showTimeStateChangeError: boolean;
  customValue: TimeRange;
  gridsterTranslateValues: { x: number; y: number; z: number };
  label: string;
  granularityPicklist: any; //eslint-disable-line
  selectedGranularity: string;
  customStartTime: string;
  customEndTime: string;
  timeZone: string;
  //Holds page refresh paused or resumed
  autoRefreshEnabled = true;
  // Holds primary status of autorefresh based on actual selection
  autoRefreshPrimaryStatus = true;
  pageTriaged = false;
  selectedLabel: string;
  constructor(
    private readonly dateFormatterService: DateFormatterService,
    private readonly elementRef: ElementRef,
    private readonly router: Router,
    private readonly timePickerService: TimePickerService,
    private readonly valuesExchangeProvider: ValuesExchangeProvider,
    private readonly notificationService: NotificationService,
    private readonly autoRefreshService: AutoRefreshService,
    private readonly userService: UserService
  ) {
    this.granularityPicklist = [
      { label: 'Minutes', value: 'timestamp-by-minute', active: true },
      { label: 'Hours', value: 'timestamp-by-hour', active: true },
      { label: 'Days', value: 'timestamp-by-day-of-month', active: true },
      { label: 'Months', value: 'timestamp-by-month', active: true }
    ];
  }

  ngOnInit() {
    if (this.utc) {
      this.maxDate = this.maxDate.seconds(0).milliseconds(0).utc();
    }
    if (!this.timePickerOptions) {
      this.timePickerOptions = this.userService.isFusionDashboardProject()
        ? TIMEPICKER_FUSION_OPTIONS
        : TIMEPICKER_OPTIONS;
    }
    if (!this.granularity) {
      this.granularity = DEFAULT_GRANULARITY;
    }
    this.timePickerSubscription = this.timePickerService
      .getActiveTimeRange$()
      .subscribe((item: TimeRange) => this.loadTimePickerSelection(item));

    if (!this.utc) {
      // condition for not use this subscription for fusion analytics
      this.timePickerEventSubscription = this.valuesExchangeProvider.registerInterest(
        'update_time_picker',
        range => {
          this.timePickerService.updateLast1HourRange(range);
        }
      );
    }
    this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeTimeRangeSelector();
      });

    this.autoRefreshSubscription = this.valuesExchangeProvider.registerInterest(
      'reset_auto_refresh',
      () => {
        this.autoRefreshEnabled = true;
        this.autoRefreshPrimaryStatus = true;
        this.pageTriaged = false;
      }
    );

    this.pageTriagedSubscription = this.valuesExchangeProvider.registerInterest(
      'auto_refresh_page_triaged',
      item => {
        if (item) {
          this.pageTriaged = item.pageTriaged;
          // when auto refresh already stopped nothing to change the status. we need retain existing state.
          if (this.autoRefreshPrimaryStatus) {
            this.autoRefreshEnabled = !this.pageTriaged;
            if (this.isLast1h && item.notifyUpdate) {
              this.notifyAutoRefreshUpdate();
            }
          }
        }
      }
    );
    this.clearChartZoom();
    this.clearAutoTimepicker();
  }

  clearChartZoom(): void {
    const graphZoomed = sessionStorage.getItem('graphZoomed');
    if (graphZoomed === 'true') {
      const lastTimeRange: TimeRange = this.timePickerService.lastTimeRange;
      this.timePickerService.replaceTimeRangeOnChartZoom(lastTimeRange, true, false);
    }
  }
  clearAutoTimepicker(): void {
    const autoTimepicker = sessionStorage.getItem('autoTimepicker');
    if (autoTimepicker === 'true') {
      const lastTimeRange: TimeRange = this.timePickerService.lastTimeRange;
      this.timePickerService.replaceTimeRangeOnAutoTimepicker(lastTimeRange, true, false);
    }
  }

  ngOnDestroy() {
    this.timePickerSubscription.unsubscribe();
    if (this.timePickerEventSubscription) {
      this.timePickerEventSubscription.unsubscribe();
    }

    if (this.selectedListItemSubscription) {
      this.selectedListItemSubscription.unsubscribe();
    }
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
    }
    if (this.pageTriagedSubscription) {
      this.pageTriagedSubscription.unsubscribe();
    }
  }

  get isCustom(): boolean {
    return this.timePickerSelection.timeRange.key === TimeRangeKey.CUSTOM;
  }

  get isLast1h(): boolean {
    return (
      this.timePickerSelection.timeRange.key === TimeRangeKey.LAST_1H ||
      this.timePickerSelection.timeRange.key === TimeRangeKey.LAST_1H_CACHED
    );
  }

  get isLastTimeRange(): boolean {
    return this.timePickerSelection.timeRange.key !== TimeRangeKey.CUSTOM && !this.isLast1h;
  }

  public setTimeRange(timeRange: TimeRange): void {
    const timeRangeClone: TimeRange = cloneDeep(timeRange);
    timeRangeClone.granularity = this._findGranularity(timeRangeClone);
    if (this.utc && timeRange.key === 'LAST_30DAYS') {
      timeRangeClone.snapToUTCDayBoundaries();
    }
    if (this.utc && timeRange.key === 'CUSTOM' && timeRange.dimension === 'timestamp-by-hour') {
      timeRange.minutesToZero();
    }
    const currentGranularity = this.granularityPicklist.find(item => {
      return item.value === this.selectedGranularity;
    });
    if (this.utc) {
      sessionStorage.setItem('granularity', currentGranularity.label);
    }
    this.timePickerService.replaceTimeRange(timeRangeClone, this.utc);
  }

  public getHideDateRange(item: TimeRange): boolean {
    return item.key === TimeRangeKey.CUSTOM;
  }

  public toggleTimeRangeSelector(): void {
    if (!this.showTimeRangeSelector) {
      // determine background overlay transform value based on the gridster parent element
      const gridsterElement = this.elementRef.nativeElement.closest('gridster-item');
      this.gridsterTranslateValues = this._getDOMNodeTranslateValues(gridsterElement);
    } else {
      this.gridsterTranslateValues = null;
    }
    this.showTimeRangeSelector = !this.showTimeRangeSelector;
  }

  public closeTimeRangeSelector(): void {
    if (this.showTimeRangeSelector) {
      this.showTimeRangeSelector = false;
      this.showCalendar = false;
    }
  }

  public onCustomRangeSelect(selectedDate: { start: moment.Moment; end: moment.Moment }): void {
    let showTimeRangeError = true;
    if (selectedDate.start && selectedDate.end) {
      if (this.utc) {
        selectedDate.start.utc();
        selectedDate.end.utc();
        this.setGranularity(selectedDate.start, selectedDate.end, true);
      }
      showTimeRangeError =
        selectedDate.start.isSame(selectedDate.end) || selectedDate.start.isAfter(selectedDate.end);
    }
    this.showTimeRangeError = showTimeRangeError;

    if (!showTimeRangeError) {
      this.customValue = this.timePickerService.makeFixedTimeRange(
        selectedDate.start.toDate().getTime(),
        selectedDate.end.toDate().getTime()
      );
      this.createCustomLabel(selectedDate.start, selectedDate.end);
    }
  }

  public onValidStateChange(validState: { nkTimeInputInvalid: boolean }): void {
    this.showTimeStateChangeError = validState?.nkTimeInputInvalid || false;
  }

  public applyRange(): void {
    if (this.utc && this.customValue.key === 'CUSTOM') {
      this.customValue = this.changeCustomValue(this.customValue);
    }
    this.setTimeRange(this.customValue);
    this.closeTimeRangeSelector();
    this._setCalendarDataModel(null, null);
  }

  public getItemCSSClass(item: TimeRange): string {
    switch (item.key) {
      case TimeRangeKey.CUSTOM:
        return 'customDate';
      case TimeRangeKey.PLUS_24H:
      case TimeRangeKey.MINUS_24H:
        return 'customInterval' + (this._isIntervalEndingInFuture(item) ? ' disabled' : '');
      default:
        return 'itemList';
    }
  }

  public chooseTimePickerOption(item: TimePickerOption): void {
    sessionStorage.setItem('graphZoomed', 'false');
    sessionStorage.setItem('autoTimepicker', 'false');
    this.selectedLabel = item.label;
    const cloneTimeRange: TimeRange = cloneDeep(item.timeRange);
    if (this.utc) {
      const range = cloneTimeRange.getTimestampRange();
      range.fromTime = new Date(range.fromTime).setMinutes(0);
      range.fromTime = new Date(range.fromTime).setMilliseconds(0);
      range.toTime = new Date(range.toTime).setMinutes(0);
      range.toTime = new Date(range.toTime).setMilliseconds(0);
      this.createCustomLabel(range.fromTime, range.toTime);
      if (cloneTimeRange.key !== 'CUSTOM') {
        this.setGranularity(range.fromTime, range.toTime, false);
      }
    }
    switch (cloneTimeRange.key) {
      case TimeRangeKey.CUSTOM:
        if (this.utc) {
          this.customValue = cloneTimeRange;
        }
        this._openCalendar();
        break;
      case TimeRangeKey.MINUS_24H:
      case TimeRangeKey.PLUS_24H:
        if (this._shiftDateRange(cloneTimeRange)) {
          this.setTimeRange(this.customValue);
          this.showTimeRangeSelector = false;
        }
        break;
      default: {
        this.showCalendar = false;
        this.showTimeRangeSelector = false;
        this.timePickerService.fillTimeRange(cloneTimeRange);
        this.setTimeRange(cloneTimeRange);
        break;
      }
    }
  }

  public toggleAutoRefresh(): void {
    this.autoRefreshEnabled = !this.autoRefreshEnabled;
    this.autoRefreshPrimaryStatus = cloneDeep(this.autoRefreshEnabled);
    this.notifyAutoRefreshUpdate();
  }

  notifyAutoRefreshUpdate(): void {
    this.autoRefreshService.notifyAutoRefreshUpdate(this.autoRefreshEnabled);
    const message = this.autoRefreshEnabled ? 'Auto refresh resumed' : 'Auto refresh paused';
    this.notificationService.onSuccess(message);
  }

  getAutoRefreshTitle(): string {
    if (this.pageTriaged) {
      return 'Auto refresh disabled when triage is in progress';
    }
    return this.autoRefreshEnabled ? 'Pause auto refresh' : 'Resume auto refresh';
  }

  //eslint-disable-next-line
  changeCustomValue(custom: TimeRange): any {
    custom['from'] = this.calendarModel.start['_d'];
    custom['to'] = this.calendarModel.end['_d'];
    custom['dimension'] = this.getDimension(custom);
    return custom;
  }

  //eslint-disable-next-line
  getDimension(item: any): any {
    const frangeDate = new Date(item.from).getTime();
    const trangeDate = new Date(item.to).getTime();
    const millisec = Math.abs(frangeDate - trangeDate);
    const m = Number((millisec / (1000 * 60)).toFixed(1));
    const h = Number((millisec / (1000 * 60 * 60)).toFixed(1));
    const d = Number((millisec / (1000 * 60 * 60 * 24)).toFixed(1));

    if (m <= 60) {
      return this.selectedGranularity ? this.selectedGranularity : 'timestamp-by-minute';
    } else if (h <= 24) {
      return this.selectedGranularity ? this.selectedGranularity : 'timestamp-by-hour';
    } else if (d <= 30) {
      return this.selectedGranularity ? this.selectedGranularity : 'timestamp-by-day-of-month';
    } else {
      return this.selectedGranularity ? this.selectedGranularity : 'timestamp-by-month';
    }
  }

  //eslint-disable-next-line
  setGranularity(from: any, to: any, custom?: any): void {
    const frangeDate = new Date(from).getTime();
    const trangeDate = new Date(to).getTime();
    const millisec = Math.abs(frangeDate - trangeDate);
    const m = Number((millisec / (1000 * 60)).toFixed(1));
    const h = Number((millisec / (1000 * 60 * 60)).toFixed(1));
    const d = Number((millisec / (1000 * 60 * 60 * 24)).toFixed(1));
    const TwoHours = 7200000;
    if (m <= 60) {
      this.selectedGranularity = 'timestamp-by-minute';
      this.granularityPicklist.forEach(item => {
        if (item.label === 'Minutes') {
          item.active = true;
        } else {
          item.active = false;
        }
      });
    } else if (h <= 24) {
      if (custom && millisec > TwoHours) {
        this.selectedGranularity = 'timestamp-by-hour';
      } else if (custom && millisec <= TwoHours) {
        this.selectedGranularity = 'timestamp-by-minute';
      } else if (
        custom &&
        !(
          this.selectedGranularity === 'timestamp-by-minute' ||
          this.selectedGranularity === 'timestamp-by-hour'
        )
      ) {
        this.selectedGranularity = 'timestamp-by-hour';
      } else if (!custom) {
        this.selectedGranularity = 'timestamp-by-hour';
      }
      this.granularityPicklist.forEach(item => {
        if (custom && millisec > TwoHours && item.label === 'Minutes') {
          item.active = false;
        } else if (item.label === 'Minutes' || item.label === 'Hours') {
          item.active = true;
        } else {
          item.active = false;
        }
      });
    } else if (d <= 30) {
      if (
        custom &&
        !(
          this.selectedGranularity === 'timestamp-by-day-of-month' ||
          this.selectedGranularity === 'timestamp-by-hour'
        )
      ) {
        this.selectedGranularity = 'timestamp-by-day-of-month';
      } else if (!custom) {
        this.selectedGranularity = 'timestamp-by-day-of-month';
      }
      this.granularityPicklist.forEach(item => {
        if (item.label === 'Days' || item.label === 'Hours') {
          item.active = true;
        } else {
          item.active = false;
        }
      });
    } else {
      if (
        custom &&
        !(
          this.selectedGranularity === 'timestamp-by-month' ||
          this.selectedGranularity === 'timestamp-by-day-of-month'
        )
      ) {
        this.selectedGranularity = 'timestamp-by-month';
      } else if (!custom) {
        this.selectedGranularity = 'timestamp-by-month';
      }
      this.granularityPicklist.forEach(item => {
        if (item.label === 'Days' || item.label === 'Months') {
          item.active = true;
        } else {
          item.active = false;
        }
      });
    }
  }

  //eslint-disable-next-line
  createCustomLabel(start: any, end: any): void {
    this.customStartTime = `${moment(new Date(start)).format('MMMM DD, YYYY HH:mm')} - `;
    this.customEndTime = `${moment(new Date(end)).format('MMMM DD, YYYY HH:mm')}`;
    this.timeZone = `${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
  }

  private loadTimePickerSelection(timeRange: TimeRange) {
    const granularity = sessionStorage.getItem('granularity');
    if (!granularity || granularity.length === 0) {
      sessionStorage.setItem('granularity', 'Minutes');
    }
    const foundSelection = this.timePickerOptions.find(opt => opt.timeRange.key === timeRange.key);
    if (foundSelection) {
      const selection = cloneDeep(foundSelection);
      selection.timeRange = timeRange;
      this.label = selection.rangeFormatter
        ? selection.rangeFormatter(timeRange)
        : timeRange.key === 'LAST_30DAYS'
        ? this.dateFormatterService.getUTCDateTime(timeRange.from) +
          ' - ' +
          this.dateFormatterService.getUTCDateTime(timeRange.to)
        : this._formatDateRange(timeRange);
      sessionStorage.setItem(
        'timeZone',
        this.isLast60Mins(selection.label) || selection.label === TimePickerNames.LAST_24_HOURS
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : 'UTC'
      );
      if (this.selectedLabel) {
        selection.label = this.selectedLabel;
      }
      this.timePickerSelection = selection;
      if (granularity !== 'Minutes' && this.isLast60Mins(selection.label)) {
        sessionStorage.setItem('granularity', 'Minutes');
      }
    }
  }

  /**
   * Find the granularity for a given time range
   * @param timeRange
   * @returns A value in seconds that will be truncated from the time range
   */
  private _findGranularity(timeRange: TimeRange): number {
    const granularity = this.granularity.find(item =>
      timeRange.matchesDuration(item.minDuration, item.maxDuration)
    );

    return granularity?.truncateTime ?? 0;
  }

  private isLast60Mins(label: string): boolean {
    return (
      label === TimePickerNames.LAST_60_MINS ||
      label === TimePickerNames.LAST_60_MINS_FAST ||
      label === TimePickerNames.LAST_60_MINS_SLOW
    );
  }

  private _formatDateRange(timeRange: TimeRange): string {
    timeRange.granularity = this._findGranularity(timeRange);
    if (this.utc && timeRange.key === 'LAST_30DAYS') {
      timeRange.snapToUTCDayBoundaries();
    }
    if (this.utc && timeRange.key === 'CUSTOM' && timeRange.dimension === 'timestamp-by-hour') {
      timeRange.minutesToZero();
    }
    const range = timeRange.getTimestampRange();
    const fromTime =
      this.utc && timeRange.key === 'CUSTOM'
        ? moment(range.fromTime).utc()
        : moment(range.fromTime);
    const toTime =
      this.utc && timeRange.key === 'CUSTOM' ? moment(range.toTime).utc() : moment(range.toTime);
    const from = this.dateFormatterService.getDateTime(fromTime);
    const to = this.dateFormatterService.getDateTime(toTime);
    return `${from} - ${to}`;
  }

  private _setCalendarDataModel(fromTime: number, toTime: number) {
    if (!fromTime && !toTime) {
      this.calendarModel = null;
      return;
    }
    if (this.utc) {
      this.calendarModel = {
        start: moment.utc(fromTime),
        end: moment.utc(toTime)
      };
    } else {
      this.calendarModel = {
        start: moment(fromTime),
        end: moment(toTime)
      };
    }
  }

  // timerange is always compared to the current time picker selection

  private _isIntervalEndingInFuture(timeRange: TimeRange): boolean {
    const currTime: TimeRange = this.timePickerSelection?.timeRange;
    if (currTime && (!this.isLast1h || timeRange.key === TimeRangeKey.MINUS_24H)) {
      return currTime.toTime + timeRange.getDuration() > Date.now();
    }
    return true;
  }

  private _openCalendar(): void {
    const currentTime: TimeRange = this.timePickerService.getCurrentValue();
    const range = currentTime.getTimestampRange();
    if (this.utc) {
      this.createCustomLabel(range.fromTime, range.toTime);
      this.setGranularity(range.fromTime, range.toTime, true);
    }
    this._setCalendarDataModel(range.fromTime, range.toTime);
    this.showCalendar = true;
    this.showTimeRangeError = false;
  }

  private _shiftDateRange(item: TimeRange): boolean {
    this.customValue = null;

    if (this._isIntervalEndingInFuture(item)) {
      return false;
    }

    const currentTime: TimeRange = this.timePickerSelection?.timeRange;
    const shiftMs = item.getDuration() || 0;

    if (this.isLast1h) {
      currentTime.toTime = moment(this.maxDate).startOf('minute').valueOf();
      currentTime.fromTime = moment(this.maxDate)
        .startOf('minute')
        .subtract(currentTime.timerange)
        .valueOf();
    }

    if (currentTime && currentTime.fromTime && currentTime.toTime) {
      this.customValue = this.timePickerService.makeFixedTimeRange(
        currentTime.fromTime + shiftMs,
        currentTime.toTime + shiftMs
      );

      this.customValue.fromTime = moment(
        Math.floor(this.customValue.fromTime / DEFAULT_GRANULARITY[0].truncateTime) *
          DEFAULT_GRANULARITY[0].truncateTime
      ).valueOf();

      this.customValue.toTime = moment(
        Math.floor(this.customValue.toTime / DEFAULT_GRANULARITY[0].truncateTime) *
          DEFAULT_GRANULARITY[0].truncateTime
      ).valueOf();

      return true;
    }

    console.warn('selected time is invalid', currentTime);
    return false;
  }

  /**
   * Gets computed translate values
   * @param {HTMLElement} element
   * @returns {Object}
   */
  private _getDOMNodeTranslateValues(element?: HTMLElement): { x: number; y: number; z: number } {
    if (!element) {
      return {
        x: 0,
        y: 0,
        z: 0
      };
    }

    const style: unknown = window.getComputedStyle(element);
    const matrix = style['transform'] || style['webkitTransform'] || style['mozTransform'];

    // No transform property. Simply return 0 values.
    if (matrix === 'none') {
      return {
        x: 0,
        y: 0,
        z: 0
      };
    }

    // Can either be 2d or 3d transform
    const matrixType = matrix.includes('3d') ? '3d' : '2d';
    const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');

    // 2d matrices have 6 values
    // Last 2 values are X and Y.
    // 2d matrices does not have Z value.
    if (matrixType === '2d') {
      return {
        x: matrixValues[4],
        y: matrixValues[5],
        z: 0
      };
    }

    // 3d matrices have 16 values
    // The 13th, 14th, and 15th values are X, Y, and Z
    if (matrixType === '3d') {
      return {
        x: matrixValues[12],
        y: matrixValues[13],
        z: matrixValues[14]
      };
    }
  }
}
